// What a plan includes, in the numbers the website's pricing cards show (site/assets/plans.js: estimate()). Billing
// reads the same plans table and credit weights, so a plan card in the app and on the site say the same thing.
// No imports: pure arithmetic on the config, never typed-in prices. tests/billing-profile.test.ts holds it equal to plans.js.

export type PlanRow = {
  id: string; name: string; price_amount: string | number | null; currency: string; monthly_credits: number | null;
  sending_domains: number | null; sending_mailboxes: number | null; features: { team?: boolean; outbound?: unknown } | null; managed_ad_platforms?: number | null;
};
export type Weight = { operation: string; credits: number };

export const EMAILS_PER_CONTACT = 3;
export const LEADS_PER_100 = [2, 6] as const;
const EMAIL_OPS = ['qualification', 'contact_discovery', 'email_verification'];

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export type PlanFacts = {
  id: string; name: string; price_usd: number | null; credits: number; contacts: number; emails: number; warm_leads: string | null; per_warm_lead: string | null;
  domains: number; mailboxes: number; team: boolean; managed_platforms: number;
};

/** Credits per researched contact (qualify + find + verify), from the credit weights; 0 when they are missing. */
export function creditsPerContact(weights: Weight[]): number {
  const w = new Map(weights.map((x) => [x.operation, Number(x.credits)]));
  return EMAIL_OPS.reduce((n, op) => n + (w.get(op) ?? 0), 0);
}

/** The facts of one plan; null for a plan without a list price or monthly credits (Managed: per contract). */
export function planFacts(p: PlanRow, weights: Weight[]): PlanFacts | null {
  const per = creditsPerContact(weights);
  const credits = Number(p.monthly_credits ?? 0);
  if (p.price_amount === null || !credits || !per) return null;
  const price = Number(p.price_amount);
  const contacts = Math.floor(credits / per);
  const lo = (contacts * LEADS_PER_100[0]) / 100, hi = (contacts * LEADS_PER_100[1]) / 100;
  const leadsLo = Math.round(lo), leadsHi = Math.max(1, Math.round(hi));
  return {
    id: p.id, name: p.name, price_usd: price, credits, contacts, emails: contacts * EMAILS_PER_CONTACT,
    warm_leads: leadsLo === leadsHi ? String(leadsHi) : `${Math.max(leadsLo, 1)}–${leadsHi}`,
    per_warm_lead: hi ? `${money(price / hi)}–${money(price / Math.max(lo, 1))}` : null,
    domains: Number(p.sending_domains ?? 0), mailboxes: Number(p.sending_mailboxes ?? 0), team: Boolean(p.features?.team), managed_platforms: Number(p.managed_ad_platforms ?? 0),
  };
}

/** The owner's refund rule for plans and credit packs (the ad wallet keeps its own: outlevo.com/legal/#ad-wallet-refunds). */
export const REFUND_LINE = 'Cancel any time, no refund for the current month.';

const RANK: Record<string, number> = { free: 0, builder: 1, growth: 2, managed: 3 };
const cap = (s: string) => s[0]!.toUpperCase() + s.slice(1);
const usdWhole = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

/** The line above a Choose button: the plan, the price, and what happens now (charged now / at the end of the period). */
export function chooseSummary(o: { plan: 'builder' | 'growth'; current: string; price_usd: number; period_end?: string | null }): string {
  const head = `${cap(o.plan)}, ${usdWhole(o.price_usd)} a month.`;
  const cur = RANK[o.current] ?? 0;
  if (cur > RANK[o.plan]!) {
    const end = o.period_end ? new Date(o.period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' }) : 'the end of the current period';
    return `${head} Starts on ${end}; nothing is charged now.`;
  }
  if (cur >= 1) return `${head} Charged now for the rest of this month (prorated), then ${usdWhole(o.price_usd)} each month.`;
  return `${head} Charged now, then every month until you cancel.`;
}
